import {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  useLayoutEffect,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Plus,
  Search,
  Droplets,
  Pencil,
  Trash2,
  SlidersHorizontal,
  X,
  MapPin,
  ArrowLeft,
  Table as TableIcon,
  Rows3,
  LayoutGrid,
  Layers,
  ChevronUp,
  ChevronDown,
  Building2,
} from 'lucide-react';
import { Toast } from '../../components/ui/FormField';
import { StatsCard } from '../../components/ui/StatsCard';
import { SortHeader, makeSortToggle, type SortDir } from '../../components/ui/SortHeader';
import { useCrudPage } from '../../hooks/useCrudPage';
import { usePageAction } from '../../stores/page-store';
import { useActivityStore } from '../../stores/activity-store';
import { useAuthStore } from '../../stores/auth-store';
import { wellApi, companyApi } from '../../services/api-services';
import type { Well, Company, ListQuery } from '../../types/api';

// ─────────────────────────────────────────────────────────────
// Wells Management — Operations Deck (View Modes design)
//
// Implements the "Wells View Modes" Claude Design handoff (May 2026):
//   • 4 view modes — Default Table · Compact · Card · Grouped by Company
//   • View switcher + Stats toggle in the filter toolbar
//   • Status palette: Drilling = accent · Producing = good · Scheduled = warn · Planned = dim
//   • Status circles carry a native `title` tooltip with the status name
//   • Truncated cells show the full value via a hover tooltip (TipCell)
//   • Compact view actions live as a hover gradient overlay on the right
//   • User's view choice persists in localStorage
//
// The page hosts list + filter + delete + activity. The Wells form still
// lives at /dashboard/wells/new and /dashboard/wells/edit/:id.
// ─────────────────────────────────────────────────────────────

// ── Service state ──────────────────────────────────────────────
type ServiceState = 'planned' | 'scheduled' | 'drilling' | 'producing';

interface ServiceMeta {
  state: ServiceState;
  label: string;
  color: string;
  bgTint: string;   // 15% alpha-ish — used for card status pill background
}

function getServiceState(w: Well): ServiceMeta {
  const now = Date.now();
  const mob = w.serviceDate ? new Date(w.serviceDate).getTime() : NaN;
  const rel = w.serviceReleaseDate ? new Date(w.serviceReleaseDate).getTime() : NaN;

  if (Number.isFinite(rel) && rel <= now) {
    return {
      state: 'producing', label: 'Producing',
      color: 'var(--color-good)', bgTint: 'rgba(34,139,79,0.10)',
    };
  }
  if (Number.isFinite(mob) && mob <= now) {
    return {
      state: 'drilling', label: 'Drilling',
      color: 'var(--color-accent)', bgTint: 'rgba(11,74,111,0.10)',
    };
  }
  if (Number.isFinite(mob) && mob > now) {
    return {
      state: 'scheduled', label: 'Scheduled',
      color: 'var(--color-warn)', bgTint: 'rgba(183,121,31,0.10)',
    };
  }
  return {
    state: 'planned', label: 'Planned',
    color: 'var(--color-text-dim)', bgTint: 'rgba(136,148,165,0.10)',
  };
}

// ── Date formatting (ISO yyyy-mm-dd, matches the legacy table) ─
function isoDate(d: string | null | undefined): string {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// ── View mode ──────────────────────────────────────────────────
type ViewMode = 'table' | 'compact' | 'card' | 'grouped';
const VIEW_MODES: ViewMode[] = ['table', 'compact', 'card', 'grouped'];
const VIEW_MODE_KEY = 'geo:wells:viewMode';
const KPIS_COLLAPSED_KEY = 'geo:wells:kpisCollapsed';

function loadViewMode(): ViewMode {
  try {
    const v = localStorage.getItem(VIEW_MODE_KEY) as ViewMode | null;
    if (v && VIEW_MODES.includes(v)) return v;
  } catch { /* ignore */ }
  return 'table';
}
function loadKpisCollapsed(): boolean {
  try { return localStorage.getItem(KPIS_COLLAPSED_KEY) === '1'; } catch { return false; }
}

type StatusFilter = 'all' | 'drilling' | 'producing' | 'scheduled' | 'planned';

// ─────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────

export function WellsPage() {
  const navigate = useNavigate();
  const authUser = useAuthStore((s) => s.user);
  const isSuperAdmin = authUser?.role === 'SuperAdmin';
  const authUsername = authUser?.username;
  const authCompanyId = authUser?.companyId != null ? Number(authUser.companyId) : null;
  const [searchParams] = useSearchParams();

  // Deep-link from Sites page: ?siteId=N&siteName=X
  const siteIdParam = Number(searchParams.get('siteId') ?? 0);
  const siteNameParam = searchParams.get('siteName') ?? '';
  const isSiteFiltered = siteIdParam > 0;

  // Filters
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [companyFilter, setCompanyFilter] = useState<number | ''>('');
  const [rigFilter, setRigFilter] = useState('');
  const [toolFilter, setToolFilter] = useState('');

  // View mode + KPI collapse (localStorage-persisted)
  const [viewMode, setViewModeRaw] = useState<ViewMode>(loadViewMode);
  const [kpisCollapsed, setKpisCollapsedRaw] = useState<boolean>(loadKpisCollapsed);
  const setViewMode = (v: ViewMode) => {
    setViewModeRaw(v);
    try { localStorage.setItem(VIEW_MODE_KEY, v); } catch { /* ignore */ }
  };
  const toggleKpis = () => {
    setKpisCollapsedRaw((c) => {
      const next = !c;
      try { localStorage.setItem(KPIS_COLLAPSED_KEY, next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };

  // Reference data
  const [companies, setCompanies] = useState<Company[]>([]);

  // ── Server-side fetch ──────────────────────────────────────
  const fetchFn = useCallback(
    (q: ListQuery) => {
      if (isSiteFiltered) return wellApi.getAllBySiteId(siteIdParam, q);
      if (isSuperAdmin) {
        return companyFilter !== ''
          ? wellApi.getAllByCompanyId(Number(companyFilter), q)
          : wellApi.getAll(q);
      }
      return wellApi.getAllByCompanyId(authCompanyId ?? 0, q);
    },
    [isSuperAdmin, isSiteFiltered, siteIdParam, companyFilter, authCompanyId],
  );

  const crud = useCrudPage<Well>({
    fetchFn,
    initialSortCol: 'ServiceDate',
    initialSortDir: 'Descending',
  });

  const recordActivity = useActivityStore((s) => s.record);

  useEffect(() => {
    if (isSuperAdmin) {
      companyApi.getAllActive({ limit: 1000 }).then((r) => setCompanies(r.entities ?? [])).catch(() => {});
    }
  }, [isSuperAdmin]);

  // ── Client-side filter pass on current page ───────────────
  const filteredData = useMemo(() => {
    return crud.data.filter((w) => {
      const meta = getServiceState(w);
      if (statusFilter !== 'all' && meta.state !== statusFilter) return false;
      if (rigFilter && (w.rig ?? '').toLowerCase().indexOf(rigFilter.toLowerCase()) === -1) return false;
      if (toolFilter && (w.tool?.serialNumber ?? '').toLowerCase().indexOf(toolFilter.toLowerCase()) === -1) return false;
      return true;
    });
  }, [crud.data, statusFilter, rigFilter, toolFilter]);

  const availableRigs = useMemo(() => {
    const s = new Set<string>();
    crud.data.forEach((w) => w.rig?.trim() && s.add(w.rig.trim()));
    return Array.from(s).sort();
  }, [crud.data]);

  const filtersActive =
    statusFilter !== 'all' || companyFilter !== '' || !!rigFilter || !!toolFilter;
  const clearFilters = () => {
    setStatusFilter('all');
    setCompanyFilter('');
    setRigFilter('');
    setToolFilter('');
  };

  // ── Stats — computed across the loaded page ──────────────
  const stats = useMemo(() => {
    let drilling = 0, producing = 0, recent = 0;
    const recentCutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
    crud.data.forEach((w) => {
      const meta = getServiceState(w);
      if (meta.state === 'drilling') drilling += 1;
      if (meta.state === 'producing') producing += 1;
      const created = w.createdDate ? new Date(w.createdDate).getTime() : NaN;
      if (Number.isFinite(created) && created >= recentCutoff) recent += 1;
    });
    return { total: crud.totalCount, drilling, producing, recent };
  }, [crud.data, crud.totalCount]);

  const totalPages = Math.max(1, Math.ceil(crud.totalCount / crud.pageSize));
  const currentPage = Math.floor(crud.offset / crud.pageSize) + 1;

  // ── Routing helpers ───────────────────────────────────────
  const buildFormParams = () => {
    const params = new URLSearchParams();
    if (isSiteFiltered) {
      params.set('siteId', String(siteIdParam));
      params.set('siteName', siteNameParam);
    }
    if (companyFilter !== '') params.set('companyId', String(companyFilter));
    const qs = params.toString();
    return qs ? `?${qs}` : '';
  };
  const openEdit = (w: Well) => navigate(`/dashboard/wells/edit/${w.id}${buildFormParams()}`);

  const handleSort = makeSortToggle(crud.sortCol, crud.sortDir, crud.setSortCol, crud.setSortDir);

  // ── Delete ────────────────────────────────────────────────
  const handleDelete = async (well: Well) => {
    if (!confirm(`Delete well "${well.wellName}"?`)) return;
    try {
      await wellApi.delete({ id: well.id });
      crud.showToast('Well deleted');
      recordActivity({
        type: 'delete',
        entity: 'Wells',
        label: well.wellName?.trim() || `Well #${well.id}`,
        user: authUsername,
      });
      crud.load();
    } catch {
      crud.showToast('Failed to delete well', 'error');
    }
  };

  // Publish the page's primary action into the TopBar slot.
  usePageAction(
    <PrimaryButton onClick={() => navigate(`/dashboard/wells/new${buildFormParams()}`)}>
      <Plus size={14} /> Add well
    </PrimaryButton>,
    [siteIdParam, siteNameParam, companyFilter],
  );

  // Hide Company column for non-SuperAdmin or when a single-site deep-link is active
  const showCompany = !!isSuperAdmin && !isSiteFiltered;
  const showSurfaceLocation = !isSiteFiltered;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Site context breadcrumb — only when deep-linked from Sites page */}
      {isSiteFiltered && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '10px 16px',
            background: 'var(--color-panel)',
            border: '1px solid var(--color-border)',
            borderLeft: '3px solid var(--color-accent)',
            borderRadius: 8,
            fontSize: 13,
          }}
        >
          <MapPin size={15} style={{ color: 'var(--color-accent)', flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 10.5,
                textTransform: 'uppercase',
                letterSpacing: 1.4,
                color: 'var(--color-text-dim)',
                marginRight: 8,
              }}
            >
              Filtered to location
            </span>
            <span style={{ color: 'var(--color-text)', fontWeight: 500 }}>
              {siteNameParam || `Site #${siteIdParam}`}
            </span>
          </div>
          <button
            onClick={() => navigate('/dashboard/wells')}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 10px',
              fontSize: 12,
              fontWeight: 500,
              fontFamily: 'var(--font-body)',
              color: 'var(--color-accent)',
              background: 'transparent',
              border: '1px solid var(--color-border)',
              borderRadius: 6,
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(11,74,111,0.08)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            <ArrowLeft size={12} /> All wells
          </button>
        </div>
      )}

      {/* Stats strip — collapsible */}
      {!kpisCollapsed && (
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
          <StatsCard
            label="Registered"
            value={stats.total}
            sub={isSiteFiltered ? 'Wells at this location' : 'Wells in portal'}
            accent="var(--color-accent)"
            loading={crud.loading}
          />
          <StatsCard
            label="Drilling (this page)"
            value={stats.drilling}
            sub="Service in progress"
            accent="var(--color-accent)"
            loading={crud.loading}
          />
          <StatsCard
            label="Producing (this page)"
            value={stats.producing}
            sub="Service released"
            accent="var(--color-good)"
            loading={crud.loading}
          />
          <StatsCard
            label="New (this page)"
            value={stats.recent}
            sub="Added in last 30 days"
            accent="#7c6ba8"
            loading={crud.loading}
          />
        </div>
      )}

      {/* Filter / toolbar */}
      <div
        style={{
          background: 'var(--color-panel)',
          border: '1px solid var(--color-border)',
          borderRadius: 10,
          padding: '12px 18px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        {/* Search */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '7px 12px',
            background: 'var(--color-panel-alt)',
            border: '1px solid var(--color-border)',
            borderRadius: 8,
            width: 260,
            transition: 'border-color 180ms',
          }}
          className="focus-within:!border-[var(--color-accent-bright)]"
        >
          <Search size={15} style={{ color: 'var(--color-text-dim)' }} />
          <input
            placeholder="Search by name, UWI…"
            value={crud.searchText}
            onChange={(e) => {
              crud.setSearchText(e.target.value);
              crud.setOffset(0);
            }}
            style={{
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontSize: 13.5,
              flex: 1,
              fontFamily: 'var(--font-body)',
              color: 'var(--color-text)',
              width: '100%',
            }}
          />
          {crud.searchText && (
            <button
              onClick={() => { crud.setSearchText(''); crud.setOffset(0); }}
              aria-label="Clear search"
              title="Clear search"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 18, height: 18, borderRadius: 9, padding: 0,
                background: 'transparent', border: 'none',
                color: 'var(--color-text-dim)', cursor: 'pointer', flexShrink: 0,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(15,23,32,0.08)';
                e.currentTarget.style.color = 'var(--color-text)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'var(--color-text-dim)';
              }}
            >
              <X size={13} />
            </button>
          )}
        </div>

        {/* Status segmented */}
        <StatusSegmented
          value={statusFilter}
          onChange={(v) => {
            setStatusFilter(v);
            crud.setOffset(0);
          }}
        />

        {/* Filter popover */}
        <FilterPopover
          companyFilter={companyFilter}
          rigFilter={rigFilter}
          toolFilter={toolFilter}
          availableCompanies={companies}
          availableRigs={availableRigs}
          isSuperAdmin={!!isSuperAdmin && !isSiteFiltered}
          onChangeCompany={(v) => {
            setCompanyFilter(v);
            crud.setOffset(0);
          }}
          onChangeRig={setRigFilter}
          onChangeTool={setToolFilter}
          onClear={clearFilters}
          activeCount={
            (companyFilter !== '' ? 1 : 0) + (rigFilter ? 1 : 0) + (toolFilter ? 1 : 0)
          }
        />

        {filtersActive && (
          <button
            onClick={clearFilters}
            style={{
              fontSize: 12,
              color: 'var(--color-accent)',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '6px 8px',
              borderRadius: 5,
              fontFamily: 'var(--font-body)',
              fontWeight: 500,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(11,74,111,0.08)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            Clear all
          </button>
        )}

        <div style={{ flex: 1 }} />

        {/* KPI toggle */}
        <KpiToggle collapsed={kpisCollapsed} onToggle={toggleKpis} />

        {/* View mode switcher */}
        <ViewModeSwitch value={viewMode} onChange={setViewMode} />

        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--color-text-dim)' }}>
          {crud.loading
            ? 'Loading…'
            : filtersActive
              ? `${filteredData.length} of ${crud.data.length} on page · ${crud.totalCount} total`
              : `${crud.totalCount} record${crud.totalCount === 1 ? '' : 's'}`}
        </div>
      </div>

      {/* View body */}
      {crud.loading ? (
        <PanelMessage>Loading…</PanelMessage>
      ) : filteredData.length === 0 ? (
        <PanelMessage>
          <Droplets size={32} style={{ margin: '0 auto 12px', opacity: 0.4 }} />
          <div style={{ fontSize: 14, marginBottom: 4 }}>
            {filtersActive ? 'No wells match these filters' : 'No wells found'}
          </div>
          <div style={{ fontSize: 12 }}>
            {filtersActive
              ? 'Try clearing filters or moving to another page.'
              : 'Add a new well to get started.'}
          </div>
        </PanelMessage>
      ) : viewMode === 'table' ? (
        <TableView
          wells={filteredData}
          showCompany={showCompany}
          showSurfaceLocation={showSurfaceLocation}
          isSuperAdmin={!!isSuperAdmin}
          onEdit={openEdit}
          onDelete={handleDelete}
          sortCol={crud.sortCol}
          sortDir={crud.sortDir}
          onSort={handleSort}
        />
      ) : viewMode === 'compact' ? (
        <CompactView
          wells={filteredData}
          showCompany={showCompany}
          showSurfaceLocation={showSurfaceLocation}
          isSuperAdmin={!!isSuperAdmin}
          onEdit={openEdit}
          onDelete={handleDelete}
          sortCol={crud.sortCol}
          sortDir={crud.sortDir}
          onSort={handleSort}
        />
      ) : viewMode === 'card' ? (
        <CardView
          wells={filteredData}
          isSuperAdmin={!!isSuperAdmin}
          onEdit={openEdit}
          onDelete={handleDelete}
        />
      ) : (
        <GroupedView
          wells={filteredData}
          isSuperAdmin={!!isSuperAdmin}
          onEdit={openEdit}
          onDelete={handleDelete}
          sortCol={crud.sortCol}
          sortDir={crud.sortDir}
          onSort={handleSort}
        />
      )}

      {/* Pagination footer */}
      <div
        style={{
          padding: '12px 18px',
          background: 'var(--color-panel)',
          border: '1px solid var(--color-border)',
          borderRadius: 10,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: 12,
          color: 'var(--color-text-dim)',
        }}
      >
        <div>
          Showing{' '}
          <span style={{ color: 'var(--color-text-mid)', fontWeight: 500 }}>
            {filteredData.length}
          </span>
          {filtersActive && filteredData.length !== crud.data.length && (
            <> (of {crud.data.length} on page)</>
          )}{' '}
          of{' '}
          <span style={{ color: 'var(--color-text-mid)', fontWeight: 500 }}>
            {crud.totalCount}
          </span>{' '}
          wells
        </div>
        {totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-mono)' }}>
            <PaginationButton
              onClick={() => crud.setOffset(Math.max(0, crud.offset - crud.pageSize))}
              disabled={crud.offset === 0}
            >
              ←
            </PaginationButton>
            <span style={{ padding: '0 10px', color: 'var(--color-text-mid)' }}>
              {currentPage} / {totalPages}
            </span>
            <PaginationButton
              onClick={() => crud.setOffset(crud.offset + crud.pageSize)}
              disabled={currentPage >= totalPages}
            >
              →
            </PaginationButton>
          </div>
        )}
      </div>

      {crud.toast && (
        <Toast message={crud.toast.message} type={crud.toast.type} onClose={() => crud.setToast(null)} />
      )}

      {/* Pulse animation for the Drilling status dot */}
      <style>{`@keyframes wellPulse { 0%,100% { opacity: 1 } 50% { opacity: 0.35 } }`}</style>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// View: Default Table — designer percentages 11/18/11/6/11/15/8/8/4/8
// ─────────────────────────────────────────────────────────────

interface ViewProps {
  wells: Well[];
  showCompany: boolean;
  showSurfaceLocation: boolean;
  isSuperAdmin: boolean;
  onEdit: (w: Well) => void;
  onDelete: (w: Well) => void;
  sortCol: string;
  sortDir: SortDir;
  onSort: (col: string) => void;
}

// Map column `key` → backend sort col name. Columns not in this map
// render as static (non-sortable) headers — actions, bit, etc.
const WELL_SORT_KEYS: Record<string, string> = {
  company:  'CompanyName',
  name:     'WellName',
  uwi:      'WellId',
  rig:      'Rig',
  mob:      'ServiceDate',
  location: 'SurfaceLocation',
  gti:      'GTIJobNumber',
  perigon:  'PerigonToolSerialNumber',
  bit:      'BitSize',
};

function TableView({ wells, showCompany, showSurfaceLocation, isSuperAdmin, onEdit, onDelete, sortCol, sortDir, onSort }: ViewProps) {
  // Designer percentages (sums to 100%). When optional columns are hidden,
  // their width spreads proportionally — handled by computeCols().
  const baseCols = [
    { key: 'company',  label: 'Company',          w: 11, show: showCompany },
    { key: 'name',     label: 'Well Name',        w: 18, show: true },
    { key: 'uwi',      label: 'UWI',              w: 11, show: true },
    { key: 'rig',      label: 'Rig',              w: 6,  show: true },
    { key: 'mob',      label: 'Mob / Rel',        w: 11, show: true },
    { key: 'location', label: 'Surface Location', w: 15, show: showSurfaceLocation },
    { key: 'gti',      label: 'GTI Job #',        w: 8,  show: true },
    { key: 'perigon',  label: 'Perigon S/N',      w: 8,  show: true },
    { key: 'bit',      label: 'Bit',              w: 4,  show: true },
    { key: 'actions',  label: 'Actions',          w: 8,  show: true, align: 'right' as const },
  ];
  const cols = computeCols(baseCols);

  return (
    <div
      style={{
        background: 'var(--color-panel)',
        border: '1px solid var(--color-border)',
        borderRadius: 10,
        overflow: 'hidden',
      }}
    >
      {/* Header — Companies-style typography */}
      <div
        style={{
          display: 'flex',
          padding: '0 18px',
          borderBottom: '1px solid var(--color-border)',
          background: 'var(--color-panel-alt)',
        }}
      >
        {cols.map((c) => {
          const sortKey = WELL_SORT_KEYS[c.key];
          return (
            <div
              key={c.key}
              style={{
                width: `${c.w}%`,
                padding: '14px 8px',
                fontSize: 12.5,
                fontFamily: 'var(--font-body)',
                letterSpacing: 0.3,
                color: 'var(--color-text-mid)',
                fontWeight: 600,
                textAlign: c.align,
              }}
            >
              {sortKey ? (
                <SortHeader
                  label={c.label}
                  col={sortKey}
                  activeCol={sortCol}
                  dir={sortDir}
                  onClick={onSort}
                  align={c.align}
                />
              ) : (
                c.label
              )}
            </div>
          );
        })}
      </div>

      {/* Rows */}
      {wells.map((w, i) => (
        <TableRow
          key={w.id ?? i}
          well={w}
          cols={cols}
          showCompany={showCompany}
          showSurfaceLocation={showSurfaceLocation}
          isSuperAdmin={isSuperAdmin}
          isLast={i === wells.length - 1}
          onEdit={() => onEdit(w)}
          onDelete={() => onDelete(w)}
        />
      ))}
    </div>
  );
}

function TableRow({
  well: w, cols, showCompany, showSurfaceLocation, isSuperAdmin, isLast, onEdit, onDelete,
}: {
  well: Well;
  cols: { key: string; w: number; align?: 'right' }[];
  showCompany: boolean;
  showSurfaceLocation: boolean;
  isSuperAdmin: boolean;
  isLast: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [hover, setHover] = useState(false);
  const meta = getServiceState(w);
  const mob = isoDate(w.serviceDate);
  const rel = isoDate(w.serviceReleaseDate);
  const widthFor = (key: string) => cols.find((c) => c.key === key)?.w ?? 0;

  return (
    <div
      onClick={onEdit}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        padding: '0 18px',
        borderBottom: isLast ? 'none' : '1px solid var(--color-border)',
        alignItems: 'center',
        background: hover ? 'var(--color-panel-alt)' : 'transparent',
        cursor: 'pointer',
        transition: 'background 120ms',
      }}
    >
      {showCompany && (
        <TipCell width={widthFor('company')} text={w.company?.companyName?.trim() || '—'}>
          <span style={{ fontSize: 14, color: 'var(--color-text)' }}>
            {w.company?.companyName?.trim() || '—'}
          </span>
        </TipCell>
      )}

      {/* Well Name + status (dot + label) underneath */}
      <div style={{ width: `${widthFor('name')}%`, padding: '14px 8px', minWidth: 0 }}>
        <TipCell text={w.wellName?.trim() || ''} inline>
          <div
            style={{
              fontSize: 14,
              fontWeight: 500,
              color: 'var(--color-text)',
            }}
          >
            {w.wellName?.trim() || '—'}
          </div>
        </TipCell>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
          <StatusDot meta={meta} size={5} />
          <span style={{ fontSize: 12, fontWeight: 500, color: meta.color }}>{meta.label}</span>
        </div>
      </div>

      <TipCell width={widthFor('uwi')} text={w.wellId?.trim() || '—'} mono dim>
        {w.wellId?.trim() || '—'}
      </TipCell>

      <div
        style={{
          width: `${widthFor('rig')}%`,
          padding: '14px 8px',
          fontSize: 14,
          color: w.rig?.trim() ? 'var(--color-text)' : 'var(--color-text-dim)',
          fontWeight: w.rig?.trim() ? 500 : 400,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {w.rig?.trim() || '—'}
      </div>

      {/* Mob / Rel — primary date then "rel YYYY-MM-DD" subline */}
      <div style={{ width: `${widthFor('mob')}%`, padding: '14px 8px', minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontFamily: 'var(--font-mono)',
            color: mob ? 'var(--color-text)' : 'var(--color-text-dim)',
            whiteSpace: 'nowrap',
          }}
        >
          {mob || '—'}
        </div>
        {rel && (
          <div
            style={{
              fontSize: 11,
              fontFamily: 'var(--font-mono)',
              color: 'var(--color-text-dim)',
              whiteSpace: 'nowrap',
              marginTop: 2,
            }}
          >
            rel {rel}
          </div>
        )}
      </div>

      {showSurfaceLocation && (
        <TipCell width={widthFor('location')} text={w.site?.siteName?.trim() || '—'} dim>
          {w.site?.siteName?.trim() || '—'}
        </TipCell>
      )}

      <div
        style={{
          width: `${widthFor('gti')}%`,
          padding: '14px 8px',
          fontSize: 13,
          fontFamily: 'var(--font-mono)',
          color: w.son?.trim() ? 'var(--color-accent)' : 'var(--color-text-dim)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {w.son?.trim() || '—'}
      </div>

      <TipCell width={widthFor('perigon')} text={w.tool?.serialNumber?.trim() || '—'} mono dim>
        {w.tool?.serialNumber?.trim() || '—'}
      </TipCell>

      <div
        style={{
          width: `${widthFor('bit')}%`,
          padding: '14px 8px',
          fontSize: 13,
          fontFamily: 'var(--font-mono)',
          color: w.bs?.trim() ? 'var(--color-text-mid)' : 'var(--color-text-dim)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {w.bs?.trim() || '—'}
      </div>

      {/* Always-visible actions (Default + Grouped per design) */}
      <div
        style={{
          width: `${widthFor('actions')}%`,
          padding: '14px 8px',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 6,
        }}
      >
        <IconButton title="Edit" onClick={onEdit}>
          <Pencil size={14} />
        </IconButton>
        {isSuperAdmin && (
          <IconButton title="Delete" onClick={onDelete} hoverDanger>
            <Trash2 size={14} />
          </IconButton>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// View: Compact — denser rows, single-line, hover-reveal gradient actions
// Designer percentages: 18/11/11/6/15/16/9/9/5 (totals 100%, no actions col)
// ─────────────────────────────────────────────────────────────

function CompactView({ wells, showCompany, showSurfaceLocation, isSuperAdmin, onEdit, onDelete, sortCol, sortDir, onSort }: ViewProps) {
  const baseCols = [
    { key: 'name',     label: 'Well',         w: 18, show: true },
    { key: 'company',  label: 'Company',      w: 11, show: showCompany },
    { key: 'uwi',      label: 'UWI',          w: 11, show: true },
    { key: 'rig',      label: 'Rig',          w: 6,  show: true },
    { key: 'mob',      label: 'Mob / Rel',    w: 15, show: true },
    { key: 'location', label: 'Location',     w: 16, show: showSurfaceLocation },
    { key: 'gti',      label: 'GTI Job #',    w: 9,  show: true },
    { key: 'perigon',  label: 'Perigon S/N',  w: 9,  show: true },
    { key: 'bit',      label: 'Bit',          w: 5,  show: true, align: 'right' as const },
  ];
  const cols = computeCols(baseCols);

  return (
    <div
      style={{
        background: 'var(--color-panel)',
        border: '1px solid var(--color-border)',
        borderRadius: 10,
        overflow: 'hidden',
      }}
    >
      {/* Header — Companies-style typography, slightly tighter padding for Compact */}
      <div
        style={{
          display: 'flex',
          padding: '0 18px',
          borderBottom: '1px solid var(--color-border)',
          background: 'var(--color-panel-alt)',
        }}
      >
        {cols.map((c) => {
          const sortKey = WELL_SORT_KEYS[c.key];
          return (
            <div
              key={c.key}
              style={{
                width: `${c.w}%`,
                padding: '10px 8px',
                fontSize: 12.5,
                fontFamily: 'var(--font-body)',
                letterSpacing: 0.3,
                color: 'var(--color-text-mid)',
                fontWeight: 600,
                textAlign: c.align,
              }}
            >
              {sortKey ? (
                <SortHeader
                  label={c.label}
                  col={sortKey}
                  activeCol={sortCol}
                  dir={sortDir}
                  onClick={onSort}
                  align={c.align}
                />
              ) : (
                c.label
              )}
            </div>
          );
        })}
      </div>

      {wells.map((w, i) => (
        <CompactRow
          key={w.id ?? i}
          well={w}
          cols={cols}
          showCompany={showCompany}
          showSurfaceLocation={showSurfaceLocation}
          isSuperAdmin={isSuperAdmin}
          isLast={i === wells.length - 1}
          onEdit={() => onEdit(w)}
          onDelete={() => onDelete(w)}
        />
      ))}
    </div>
  );
}

function CompactRow({
  well: w, cols, showCompany, showSurfaceLocation, isSuperAdmin, isLast, onEdit, onDelete,
}: {
  well: Well;
  cols: { key: string; w: number; align?: 'right' }[];
  showCompany: boolean;
  showSurfaceLocation: boolean;
  isSuperAdmin: boolean;
  isLast: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [hover, setHover] = useState(false);
  const meta = getServiceState(w);
  const mob = isoDate(w.serviceDate);
  const rel = isoDate(w.serviceReleaseDate);
  const widthFor = (key: string) => cols.find((c) => c.key === key)?.w ?? 0;
  const rowBg = hover ? 'var(--color-panel-alt)' : 'var(--color-panel)';

  return (
    <div
      onClick={onEdit}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        padding: '0 18px',
        position: 'relative',
        borderBottom: isLast ? 'none' : '1px solid var(--color-border)',
        alignItems: 'center',
        background: rowBg,
        cursor: 'pointer',
      }}
    >
      {/* Well + status dot (no label — denser). Wrap TipCell in flex:1 so it
          shrinks and truncates inside the cell instead of painting over Company. */}
      <div style={{ width: `${widthFor('name')}%`, padding: '10px 8px', display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <StatusDot meta={meta} size={6} />
        <TipCell text={w.wellName?.trim() || '—'} inline>
          <span
            style={{
              fontSize: 14,
              fontWeight: 500,
              color: 'var(--color-text)',
            }}
          >
            {w.wellName?.trim() || '—'}
          </span>
        </TipCell>
      </div>

      {showCompany && (
        <TipCell width={widthFor('company')} text={w.company?.companyName?.trim() || '—'} compact>
          <span style={{ fontSize: 14, color: 'var(--color-text-mid)' }}>
            {w.company?.companyName?.trim() || '—'}
          </span>
        </TipCell>
      )}

      <TipCell width={widthFor('uwi')} text={w.wellId?.trim() || '—'} mono dim compact>
        {w.wellId?.trim() || '—'}
      </TipCell>

      <div
        style={{
          width: `${widthFor('rig')}%`,
          padding: '10px 8px',
          fontSize: 14,
          color: w.rig?.trim() ? 'var(--color-text)' : 'var(--color-text-dim)',
          fontWeight: w.rig?.trim() ? 500 : 400,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {w.rig?.trim() || '—'}
      </div>

      {/* Mob → Rel inline */}
      <div
        style={{
          width: `${widthFor('mob')}%`,
          padding: '10px 8px',
          fontSize: 13,
          fontFamily: 'var(--font-mono)',
          color: 'var(--color-text-mid)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {mob || '—'}
        {rel && <span style={{ color: 'var(--color-text-dim)' }}> → {rel}</span>}
      </div>

      {showSurfaceLocation && (
        <TipCell width={widthFor('location')} text={w.site?.siteName?.trim() || '—'} dim compact>
          {w.site?.siteName?.trim() || '—'}
        </TipCell>
      )}

      <div
        style={{
          width: `${widthFor('gti')}%`,
          padding: '10px 8px',
          fontSize: 13,
          fontFamily: 'var(--font-mono)',
          color: w.son?.trim() ? 'var(--color-accent)' : 'var(--color-text-dim)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {w.son?.trim() || '—'}
      </div>

      <TipCell width={widthFor('perigon')} text={w.tool?.serialNumber?.trim() || '—'} mono dim compact>
        {w.tool?.serialNumber?.trim() || '—'}
      </TipCell>

      <div
        style={{
          width: `${widthFor('bit')}%`,
          padding: '10px 8px',
          fontSize: 13,
          fontFamily: 'var(--font-mono)',
          color: w.bs?.trim() ? 'var(--color-text-mid)' : 'var(--color-text-dim)',
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {w.bs?.trim() || '—'}
      </div>

      {/* Gradient overlay actions — only visible on hover */}
      <GradientOverlayActions
        visible={hover}
        bgColor={rowBg}
        onEdit={onEdit}
        onDelete={isSuperAdmin ? onDelete : undefined}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// View: Cards — 3-column grid, status stripe + chip, 2x3 detail grid
// ─────────────────────────────────────────────────────────────

function CardView({ wells, isSuperAdmin, onEdit, onDelete }: Omit<ViewProps, 'showCompany' | 'showSurfaceLocation' | 'sortCol' | 'sortDir' | 'onSort'>) {
  return (
    <div
      className="grid gap-3 grid-cols-1 md:grid-cols-2 xl:grid-cols-3"
    >
      {wells.map((w) => (
        <WellCard
          key={w.id}
          well={w}
          isSuperAdmin={isSuperAdmin}
          onEdit={() => onEdit(w)}
          onDelete={() => onDelete(w)}
        />
      ))}
    </div>
  );
}

function WellCard({ well: w, isSuperAdmin, onEdit, onDelete }: {
  well: Well;
  isSuperAdmin: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [hover, setHover] = useState(false);
  const meta = getServiceState(w);
  const mob = isoDate(w.serviceDate);
  const rel = isoDate(w.serviceReleaseDate);
  return (
    <div
      onClick={onEdit}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: 'var(--color-panel)',
        border: '1px solid var(--color-border)',
        borderRadius: 10,
        padding: '20px 22px 16px',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
        boxShadow: hover ? '0 4px 16px rgba(15,23,32,0.06)' : 'none',
        transition: 'box-shadow 150ms',
      }}
    >
      {/* Status stripe at the top of the card */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: meta.color,
        }}
      />

      {/* Header — name + status chip (table-aligned typography) */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            title={w.wellName?.trim() || ''}
            style={{
              fontSize: 14,
              fontWeight: 500,
              color: 'var(--color-text)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {w.wellName?.trim() || '—'}
          </div>
          <div
            title={w.company?.companyName?.trim() || ''}
            style={{
              fontSize: 13,
              color: 'var(--color-text-mid)',
              marginTop: 3,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {w.company?.companyName?.trim() || '—'}
          </div>
        </div>
        <span
          title={meta.label}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '4px 10px',
            borderRadius: 12,
            background: meta.bgTint,
            color: meta.color,
            fontSize: 12,
            fontWeight: 600,
            flexShrink: 0,
            fontFamily: 'var(--font-body)',
            letterSpacing: 0.2,
          }}
        >
          <span
            style={{
              width: 5, height: 5, borderRadius: 3, background: meta.color,
              animation: meta.state === 'drilling' ? 'wellPulse 1.6s ease-in-out infinite' : undefined,
            }}
          />
          {meta.label}
        </span>
      </div>

      {/* Detail grid: UWI · Rig · Mob · Location · Perigon · GTI */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 18px', marginTop: 14 }}>
        <CardField label="UWI" value={w.wellId} mono />
        <CardField label="Rig" value={w.rig} bold />
        <CardField label="Mob Date" value={mob} mono />
        <CardField label="Location" value={w.site?.siteName} dim title={w.site?.siteName} />
        <CardField label="Perigon S/N" value={w.tool?.serialNumber} mono />
        <CardField label="GTI Job #" value={w.son} mono accent />
      </div>

      {/* Footer — Bit · Rel · actions */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginTop: 14,
          paddingTop: 10,
          borderTop: '1px solid var(--color-border)',
        }}
      >
        {w.bs?.trim() && (
          <span
            style={{
              fontSize: 12,
              fontFamily: 'var(--font-mono)',
              color: 'var(--color-text-mid)',
              background: 'var(--color-panel-alt)',
              padding: '3px 9px',
              borderRadius: 5,
            }}
          >
            Bit: {w.bs.trim()}
          </span>
        )}
        {rel && (
          <span style={{ fontSize: 12, color: 'var(--color-text-dim)', fontFamily: 'var(--font-mono)' }}>
            Rel: {rel}
          </span>
        )}
        <div style={{ flex: 1 }} />
        <IconButton title="Edit" onClick={onEdit} size={24}>
          <Pencil size={12} />
        </IconButton>
        {isSuperAdmin && (
          <IconButton title="Delete" onClick={onDelete} hoverDanger size={24}>
            <Trash2 size={12} />
          </IconButton>
        )}
      </div>
    </div>
  );
}

function CardField({
  label, value, mono, dim, bold, accent, title,
}: {
  label: string;
  value?: string | null;
  mono?: boolean;
  dim?: boolean;
  bold?: boolean;
  accent?: boolean;
  title?: string;
}) {
  const v = value?.trim();
  const empty = !v;
  return (
    <div style={{ minWidth: 0 }}>
      {/* Label — same treatment as the table column headers
          (body 12.5pt / weight 600 / text-mid / letterSpacing 0.3, NOT mono-uppercase) */}
      <div
        style={{
          fontSize: 12.5,
          fontFamily: 'var(--font-body)',
          letterSpacing: 0.3,
          color: 'var(--color-text-mid)',
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      {/* Value — matches table body cells: 14pt body / 13pt mono */}
      <div
        title={title}
        style={{
          fontSize: mono ? 13 : 14,
          fontFamily: mono ? 'var(--font-mono)' : 'var(--font-body)',
          color: empty
            ? 'var(--color-text-dim)'
            : accent
              ? 'var(--color-accent)'
              : dim
                ? 'var(--color-text-mid)'
                : 'var(--color-text)',
          fontWeight: bold && !empty ? 500 : 400,
          marginTop: 3,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {v || '—'}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// View: Grouped by Company — per-group panel with column headers + status summary
// ─────────────────────────────────────────────────────────────

function GroupedView({ wells, isSuperAdmin, onEdit, onDelete, sortCol, sortDir, onSort }: Omit<ViewProps, 'showCompany' | 'showSurfaceLocation'>) {
  // Group by company; preserve insertion order so the displayed list stays sorted as it came.
  const groups = useMemo(() => {
    const map = new Map<string, Well[]>();
    wells.forEach((w) => {
      const key = w.company?.companyName?.trim() || '— Unassigned —';
      const existing = map.get(key);
      if (existing) existing.push(w);
      else map.set(key, [w]);
    });
    return Array.from(map.entries());
  }, [wells]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {groups.map(([company, list]) => (
        <CompanyGroup
          key={company}
          company={company}
          wells={list}
          isSuperAdmin={isSuperAdmin}
          onEdit={onEdit}
          onDelete={onDelete}
          sortCol={sortCol}
          sortDir={sortDir}
          onSort={onSort}
        />
      ))}
    </div>
  );
}

function CompanyGroup({
  company, wells, isSuperAdmin, onEdit, onDelete, sortCol, sortDir, onSort,
}: {
  company: string;
  wells: Well[];
  isSuperAdmin: boolean;
  onEdit: (w: Well) => void;
  onDelete: (w: Well) => void;
  sortCol: string;
  sortDir: SortDir;
  onSort: (col: string) => void;
}) {
  // Group columns intentionally drop the Company column (it's in the header) and
  // give the freed space to Well Name / Location.
  const baseCols = [
    { key: 'name',     label: 'Well Name',   w: 22, show: true },
    { key: 'uwi',      label: 'UWI',         w: 13, show: true },
    { key: 'rig',      label: 'Rig',         w: 7,  show: true },
    { key: 'mob',      label: 'Mob / Rel',   w: 12, show: true },
    { key: 'location', label: 'Location',    w: 18, show: true },
    { key: 'gti',      label: 'GTI Job #',   w: 8,  show: true },
    { key: 'perigon',  label: 'Perigon S/N', w: 10, show: true },
    { key: 'bit',      label: 'Bit',         w: 4,  show: true, align: 'right' as const },
    { key: 'actions',  label: 'Actions',     w: 6,  show: true, align: 'right' as const },
  ];
  const cols = computeCols(baseCols);

  // Status summary — counts per state, shown in the group header
  const statusCounts = useMemo(() => {
    const counts: Record<string, { count: number; color: string }> = {};
    wells.forEach((w) => {
      const m = getServiceState(w);
      if (!counts[m.label]) counts[m.label] = { count: 0, color: m.color };
      counts[m.label].count += 1;
    });
    return Object.entries(counts);
  }, [wells]);

  return (
    <div
      style={{
        background: 'var(--color-panel)',
        border: '1px solid var(--color-border)',
        borderRadius: 10,
        overflow: 'hidden',
      }}
    >
      {/* Group header — company name + total + per-status summary */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '12px 18px',
          background: 'var(--color-panel-alt)',
          borderBottom: '1px solid var(--color-border)',
        }}
      >
        <Building2 size={16} style={{ color: 'var(--color-accent)', flexShrink: 0 }} />
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text)' }}>{company}</div>
        <span
          style={{
            fontSize: 10,
            fontFamily: 'var(--font-mono)',
            color: 'var(--color-text-dim)',
            background: 'var(--color-panel)',
            border: '1px solid var(--color-border)',
            padding: '2px 8px',
            borderRadius: 10,
            fontWeight: 600,
            letterSpacing: 0.3,
          }}
        >
          {wells.length} well{wells.length === 1 ? '' : 's'}
        </span>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', gap: 12 }}>
          {statusCounts.map(([label, info]) => (
            <div
              key={label}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                fontSize: 10.5,
                color: 'var(--color-text-dim)',
              }}
            >
              <span style={{ width: 6, height: 6, borderRadius: 3, background: info.color }} />
              {info.count} {label}
            </div>
          ))}
        </div>
      </div>

      {/* Column headers within the group — Companies-style typography */}
      <div
        style={{
          display: 'flex',
          padding: '0 18px',
          borderBottom: '1px solid var(--color-border)',
          background: 'var(--color-panel)',
        }}
      >
        {cols.map((c) => {
          const sortKey = WELL_SORT_KEYS[c.key];
          return (
            <div
              key={c.key}
              style={{
                width: `${c.w}%`,
                padding: '10px 8px',
                fontSize: 12.5,
                fontFamily: 'var(--font-body)',
                letterSpacing: 0.3,
                color: 'var(--color-text-mid)',
                fontWeight: 600,
                textAlign: c.align,
              }}
            >
              {sortKey ? (
                <SortHeader
                  label={c.label}
                  col={sortKey}
                  activeCol={sortCol}
                  dir={sortDir}
                  onClick={onSort}
                  align={c.align}
                />
              ) : (
                c.label
              )}
            </div>
          );
        })}
      </div>

      {/* Wells in this group */}
      {wells.map((w, i) => (
        <GroupedRow
          key={w.id ?? i}
          well={w}
          cols={cols}
          isSuperAdmin={isSuperAdmin}
          isLast={i === wells.length - 1}
          onEdit={() => onEdit(w)}
          onDelete={() => onDelete(w)}
        />
      ))}
    </div>
  );
}

function GroupedRow({
  well: w, cols, isSuperAdmin, isLast, onEdit, onDelete,
}: {
  well: Well;
  cols: { key: string; w: number; align?: 'right' }[];
  isSuperAdmin: boolean;
  isLast: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [hover, setHover] = useState(false);
  const meta = getServiceState(w);
  const mob = isoDate(w.serviceDate);
  const rel = isoDate(w.serviceReleaseDate);
  const widthFor = (key: string) => cols.find((c) => c.key === key)?.w ?? 0;
  return (
    <div
      onClick={onEdit}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        padding: '0 18px',
        borderBottom: isLast ? 'none' : '1px solid var(--color-border)',
        alignItems: 'center',
        background: hover ? 'var(--color-panel-alt)' : 'transparent',
        cursor: 'pointer',
        transition: 'background 120ms',
      }}
    >
      <div
        style={{
          width: `${widthFor('name')}%`,
          padding: '12px 8px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 8,
          minWidth: 0,
        }}
      >
        <StatusDot meta={meta} size={6} style={{ marginTop: 6 }} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <TipCell text={w.wellName?.trim() || '—'} inline>
            <div
              style={{
                fontSize: 14,
                fontWeight: 500,
                color: 'var(--color-text)',
              }}
            >
              {w.wellName?.trim() || '—'}
            </div>
          </TipCell>
          <div style={{ fontSize: 12, color: meta.color, fontWeight: 500, marginTop: 2 }}>
            {meta.label}
          </div>
        </div>
      </div>

      <TipCell width={widthFor('uwi')} text={w.wellId?.trim() || '—'} mono dim compact>
        {w.wellId?.trim() || '—'}
      </TipCell>

      <div
        style={{
          width: `${widthFor('rig')}%`,
          padding: '12px 8px',
          fontSize: 14,
          color: w.rig?.trim() ? 'var(--color-text)' : 'var(--color-text-dim)',
          fontWeight: w.rig?.trim() ? 500 : 400,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {w.rig?.trim() || '—'}
      </div>

      <div style={{ width: `${widthFor('mob')}%`, padding: '12px 8px', minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontFamily: 'var(--font-mono)',
            color: mob ? 'var(--color-text)' : 'var(--color-text-dim)',
            whiteSpace: 'nowrap',
          }}
        >
          {mob || '—'}
        </div>
        {rel && (
          <div
            style={{
              fontSize: 11,
              fontFamily: 'var(--font-mono)',
              color: 'var(--color-text-dim)',
              whiteSpace: 'nowrap',
              marginTop: 2,
            }}
          >
            rel {rel}
          </div>
        )}
      </div>

      <TipCell width={widthFor('location')} text={w.site?.siteName?.trim() || '—'} dim compact>
        {w.site?.siteName?.trim() || '—'}
      </TipCell>

      <div
        style={{
          width: `${widthFor('gti')}%`,
          padding: '12px 8px',
          fontSize: 13,
          fontFamily: 'var(--font-mono)',
          color: w.son?.trim() ? 'var(--color-accent)' : 'var(--color-text-dim)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {w.son?.trim() || '—'}
      </div>

      <TipCell width={widthFor('perigon')} text={w.tool?.serialNumber?.trim() || '—'} mono dim compact>
        {w.tool?.serialNumber?.trim() || '—'}
      </TipCell>

      <div
        style={{
          width: `${widthFor('bit')}%`,
          padding: '12px 8px',
          fontSize: 13,
          fontFamily: 'var(--font-mono)',
          color: w.bs?.trim() ? 'var(--color-text-mid)' : 'var(--color-text-dim)',
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {w.bs?.trim() || '—'}
      </div>

      <div
        style={{
          width: `${widthFor('actions')}%`,
          padding: '12px 8px',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 6,
        }}
      >
        <IconButton title="Edit" onClick={onEdit} size={26}>
          <Pencil size={12} />
        </IconButton>
        {isSuperAdmin && (
          <IconButton title="Delete" onClick={onDelete} hoverDanger size={26}>
            <Trash2 size={12} />
          </IconButton>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Shared helpers
// ─────────────────────────────────────────────────────────────

// computeCols — drop hidden columns and redistribute their width
// proportionally across the rest so that each view always fills 100%.
function computeCols<T extends { w: number; show: boolean }>(base: T[]): T[] {
  const visible = base.filter((c) => c.show);
  const visSum = visible.reduce((s, c) => s + c.w, 0);
  if (visSum === 0) return visible;
  const factor = 100 / visSum;
  return visible.map((c) => ({ ...c, w: c.w * factor }));
}

// StatusDot — coloured circle with a native `title` tooltip; Drilling pulses.
function StatusDot({
  meta, size = 6, style,
}: {
  meta: ServiceMeta;
  size?: number;
  style?: CSSProperties;
}) {
  return (
    <span
      title={meta.label}
      style={{
        width: size,
        height: size,
        borderRadius: size / 2,
        background: meta.color,
        display: 'inline-block',
        flexShrink: 0,
        cursor: 'default',
        animation: meta.state === 'drilling' ? 'wellPulse 1.6s ease-in-out infinite' : undefined,
        ...style,
      }}
    />
  );
}

// TipCell — table cell that shows a dark hover tooltip with the full text when
// its content is truncated by ellipsis. Mirrors the designer's TipCell.
// When `inline` is true, no width/padding wrapper is added (use inside a parent
// that already controls layout, e.g. the Well Name column).
function TipCell({
  children, text, width, mono, dim, compact, inline,
}: {
  children: ReactNode;
  text: string;
  width?: number;        // percentage
  mono?: boolean;
  dim?: boolean;
  compact?: boolean;     // smaller padding for Compact view
  inline?: boolean;
}) {
  const [show, setShow] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const onEnter = () => {
    const el = ref.current;
    if (el && el.scrollWidth > el.clientWidth + 1) setShow(true);
  };
  const onLeave = () => setShow(false);

  const wrapperStyle: CSSProperties = inline
    ? { position: 'relative', flex: 1, minWidth: 0 }
    : {
        width: width != null ? `${width}%` : undefined,
        padding: compact ? '10px 8px' : '14px 8px',
        position: 'relative',
        minWidth: 0,
      };

  // The ref div is always the truncating element — required so that scrollWidth
  // detection works AND so the ellipsis renders at the cell boundary, not the
  // child span's natural width (which is what caused well names to paint over
  // neighbouring columns in Compact view).
  const innerStyle: CSSProperties = inline
    ? {
        width: '100%',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }
    : {
        fontFamily: mono ? 'var(--font-mono)' : 'var(--font-body)',
        fontSize: compact ? 13 : 14,
        color: dim ? 'var(--color-text-mid)' : 'var(--color-text)',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      };

  return (
    <div style={wrapperStyle} onMouseEnter={onEnter} onMouseLeave={onLeave}>
      <div ref={ref} style={innerStyle}>
        {children}
      </div>
      {show && (
        <div
          style={{
            position: 'absolute',
            bottom: '100%',
            left: 6,
            marginBottom: 4,
            zIndex: 50,
            background: '#0c1520',
            color: '#e8edf4',
            padding: '6px 10px',
            borderRadius: 6,
            fontSize: 11.5,
            fontFamily: 'var(--font-body)',
            whiteSpace: 'nowrap',
            maxWidth: 360,
            boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
            pointerEvents: 'none',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {text}
        </div>
      )}
    </div>
  );
}

// GradientOverlayActions — slides in from the right on hover. The gradient
// transitions from transparent to the row background so it cleanly hides the
// content underneath without a hard edge.
function GradientOverlayActions({
  visible, bgColor, onEdit, onDelete,
}: {
  visible: boolean;
  bgColor: string;
  onEdit: () => void;
  onDelete?: () => void;
}) {
  return (
    <div
      style={{
        position: 'absolute',
        right: 0,
        top: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        paddingRight: 14,
        paddingLeft: 40,
        background: `linear-gradient(90deg, transparent 0%, ${bgColor} 30%, ${bgColor} 100%)`,
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? 'auto' : 'none',
        transition: 'opacity 150ms',
        zIndex: 5,
      }}
    >
      <IconButton title="Edit" onClick={onEdit} size={26}>
        <Pencil size={12} />
      </IconButton>
      {onDelete && (
        <IconButton title="Delete" onClick={onDelete} hoverDanger size={26}>
          <Trash2 size={12} />
        </IconButton>
      )}
    </div>
  );
}

function PanelMessage({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        background: 'var(--color-panel)',
        border: '1px solid var(--color-border)',
        borderRadius: 10,
        padding: 60,
        textAlign: 'center',
        color: 'var(--color-text-dim)',
        fontSize: 13,
      }}
    >
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// View switcher + KPI toggle
// ─────────────────────────────────────────────────────────────

function ViewModeSwitch({ value, onChange }: { value: ViewMode; onChange: (v: ViewMode) => void }) {
  const opts: { id: ViewMode; tip: string; Icon: typeof TableIcon }[] = [
    { id: 'table',   tip: 'Default table',     Icon: TableIcon },
    { id: 'compact', tip: 'Compact table',     Icon: Rows3 },
    { id: 'card',    tip: 'Card view',         Icon: LayoutGrid },
    { id: 'grouped', tip: 'Grouped by company', Icon: Layers },
  ];
  return (
    <div
      style={{
        display: 'inline-flex',
        padding: 3,
        background: 'var(--color-panel-alt)',
        border: '1px solid var(--color-border)',
        borderRadius: 7,
        gap: 1,
      }}
    >
      {opts.map((o) => {
        const active = value === o.id;
        return (
          <button
            key={o.id}
            title={o.tip}
            aria-label={o.tip}
            aria-pressed={active}
            onClick={() => onChange(o.id)}
            style={{
              width: 30,
              height: 26,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 5,
              border: 'none',
              cursor: 'pointer',
              background: active ? 'var(--color-accent)' : 'transparent',
              color: active ? '#fff' : 'var(--color-text-mid)',
              transition: 'background 120ms, color 120ms',
            }}
          >
            <o.Icon size={15} />
          </button>
        );
      })}
    </div>
  );
}

function KpiToggle({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  return (
    <button
      onClick={onToggle}
      title={collapsed ? 'Show stats' : 'Hide stats'}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '5px 10px',
        background: 'var(--color-panel-alt)',
        border: '1px solid var(--color-border)',
        borderRadius: 6,
        fontSize: 11,
        color: 'var(--color-text-mid)',
        cursor: 'pointer',
        fontFamily: 'var(--font-mono)',
        letterSpacing: 0.4,
      }}
    >
      {collapsed ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
      Stats
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
// Shared primitives (matching CompaniesPage / UsersPage)
// ─────────────────────────────────────────────────────────────

function IconButton({
  children, onClick, title, hoverDanger, size = 30,
}: {
  children: ReactNode;
  onClick: () => void;
  title: string;
  hoverDanger?: boolean;
  size?: number;
}) {
  const [hover, setHover] = useState(false);
  const idleBg = 'var(--color-panel)';
  const idleBorder = 'var(--color-border)';
  const idleColor = hoverDanger ? '#a14a47' : 'var(--color-accent)';
  const hoverBg = hoverDanger ? 'rgba(185,28,28,0.08)' : 'rgba(11,74,111,0.08)';
  const hoverBorder = hoverDanger ? 'rgba(185,28,28,0.35)' : 'var(--color-accent)';
  const hoverColor = hoverDanger ? 'var(--color-bad)' : 'var(--color-accent-hover)';
  return (
    <button
      title={title}
      aria-label={title}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: size,
        height: size,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 6,
        border: `1px solid ${hover ? hoverBorder : idleBorder}`,
        cursor: 'pointer',
        background: hover ? hoverBg : idleBg,
        color: hover ? hoverColor : idleColor,
        transition: 'background 120ms, color 120ms, border-color 120ms, transform 120ms',
        transform: hover ? 'translateY(-1px)' : 'none',
        boxShadow: hover ? '0 2px 6px rgba(15,23,32,0.08)' : 'none',
      }}
    >
      {children}
    </button>
  );
}

function PrimaryButton({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        background: hover ? 'var(--color-accent-hover)' : 'var(--color-accent)',
        color: '#fff',
        border: 'none',
        padding: '9px 14px',
        borderRadius: 7,
        fontSize: 13,
        fontWeight: 500,
        fontFamily: 'var(--font-body)',
        cursor: 'pointer',
        transition: 'background 120ms',
      }}
    >
      {children}
    </button>
  );
}

function PaginationButton({
  children, onClick, disabled,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        width: 26,
        height: 26,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid var(--color-border)',
        background: 'var(--color-panel)',
        color: disabled ? 'var(--color-text-dim)' : 'var(--color-text-mid)',
        borderRadius: 4,
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: 12,
        opacity: disabled ? 0.4 : 1,
      }}
    >
      {children}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
// Status segmented + Filter popover (kept identical to prior version)
// ─────────────────────────────────────────────────────────────

function StatusSegmented({
  value, onChange,
}: {
  value: StatusFilter;
  onChange: (v: StatusFilter) => void;
}) {
  const opts: { v: StatusFilter; label: string; dot?: string }[] = [
    { v: 'all', label: 'All' },
    { v: 'drilling',  label: 'Drilling',  dot: 'var(--color-accent)' },
    { v: 'producing', label: 'Producing', dot: 'var(--color-good)' },
    { v: 'scheduled', label: 'Scheduled', dot: 'var(--color-warn)' },
    { v: 'planned',   label: 'Planned',   dot: 'var(--color-text-dim)' },
  ];
  return (
    <div
      style={{
        display: 'inline-flex',
        padding: 3,
        background: 'var(--color-panel-alt)',
        border: '1px solid var(--color-border)',
        borderRadius: 8,
        gap: 2,
      }}
    >
      {opts.map((o) => {
        const active = value === o.v;
        return (
          <button
            key={o.v}
            onClick={() => onChange(o.v)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '5px 12px',
              border: 'none',
              borderRadius: 6,
              fontSize: 12.5,
              fontWeight: 500,
              fontFamily: 'var(--font-body)',
              cursor: 'pointer',
              background: active ? 'var(--color-panel)' : 'transparent',
              color: active ? 'var(--color-text)' : 'var(--color-text-mid)',
              boxShadow: active ? '0 1px 2px rgba(15,23,32,0.06)' : 'none',
              transition: 'all 120ms',
            }}
          >
            {o.dot && (
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 3,
                  background: o.dot,
                  display: 'inline-block',
                }}
              />
            )}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

function FilterPopover({
  companyFilter, rigFilter, toolFilter,
  availableCompanies, availableRigs,
  isSuperAdmin,
  onChangeCompany, onChangeRig, onChangeTool,
  onClear, activeCount,
}: {
  companyFilter: number | '';
  rigFilter: string;
  toolFilter: string;
  availableCompanies: Company[];
  availableRigs: string[];
  isSuperAdmin: boolean;
  onChangeCompany: (v: number | '') => void;
  onChangeRig: (v: string) => void;
  onChangeTool: (v: string) => void;
  onClear: () => void;
  activeCount: number;
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const POP_WIDTH = 340;

  const updateCoords = useCallback(() => {
    const rect = btnRef.current?.getBoundingClientRect();
    if (!rect) return;
    const top = rect.bottom + 8;
    const desiredLeft = rect.left;
    const maxLeft = window.innerWidth - POP_WIDTH - 12;
    const left = Math.max(12, Math.min(desiredLeft, maxLeft));
    setCoords({ top, left });
  }, []);

  useLayoutEffect(() => {
    if (open) updateCoords();
  }, [open, updateCoords]);

  useEffect(() => {
    if (!open) return;
    const onScroll = () => updateCoords();
    const onResize = () => updateCoords();
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t) || btnRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, updateCoords]);

  const isActive = activeCount > 0;

  return (
    <>
      <button
        ref={btnRef}
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 7,
          padding: '7px 12px',
          fontSize: 13,
          fontWeight: 500,
          fontFamily: 'var(--font-body)',
          cursor: 'pointer',
          background: open ? 'var(--color-panel-alt)' : 'var(--color-panel)',
          color: isActive ? 'var(--color-accent)' : 'var(--color-text-mid)',
          border: `1px solid ${isActive ? 'var(--color-accent)' : 'var(--color-border)'}`,
          borderRadius: 7,
          transition: 'all 120ms',
        }}
      >
        <SlidersHorizontal size={14} />
        Filter
        {isActive && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 18,
              height: 18,
              padding: '0 5px',
              borderRadius: 9,
              background: 'var(--color-accent)',
              color: '#fff',
              fontSize: 10.5,
              fontWeight: 700,
              fontFamily: 'var(--font-mono)',
              lineHeight: 1,
            }}
          >
            {activeCount}
          </span>
        )}
      </button>

      {open && coords && createPortal(
        <div
          ref={popRef}
          style={{
            position: 'fixed',
            top: coords.top,
            left: coords.left,
            width: POP_WIDTH,
            background: 'var(--color-panel)',
            border: '1px solid var(--color-border)',
            borderRadius: 10,
            boxShadow: '0 12px 32px rgba(15,23,32,0.12)',
            zIndex: 100,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 16px',
              borderBottom: '1px solid var(--color-border)',
            }}
          >
            <div
              style={{
                fontSize: 10.5,
                textTransform: 'uppercase',
                letterSpacing: 1.2,
                color: 'var(--color-text-dim)',
                fontFamily: 'var(--font-mono)',
                fontWeight: 500,
              }}
            >
              Filter by
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close"
              style={{
                width: 22,
                height: 22,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--color-text-dim)',
                borderRadius: 4,
              }}
            >
              <X size={14} />
            </button>
          </div>

          <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
            {isSuperAdmin && (
              <PopoverField label="Company">
                <FilterSelect
                  value={companyFilter === '' ? '' : String(companyFilter)}
                  onChange={(v) => onChangeCompany(v === '' ? '' : Number(v))}
                  placeholder="All companies"
                  options={availableCompanies.map((c) => ({ value: String(c.id), label: c.companyName }))}
                />
              </PopoverField>
            )}
            <PopoverField label="Rig">
              <FilterSelect
                value={rigFilter}
                onChange={onChangeRig}
                placeholder={availableRigs.length ? 'All rigs' : '— none on page —'}
                options={availableRigs.map((r) => ({ value: r, label: r }))}
                disabled={availableRigs.length === 0}
              />
            </PopoverField>
            <PopoverField label="Tool Serial Number">
              <FilterText value={toolFilter} onChange={onChangeTool} placeholder="S/N contains…" />
            </PopoverField>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '10px 16px',
              borderTop: '1px solid var(--color-border)',
              background: 'var(--color-panel-alt)',
            }}
          >
            <button
              onClick={onClear}
              disabled={activeCount === 0}
              style={{
                fontSize: 12,
                color: activeCount === 0 ? 'var(--color-text-dim)' : 'var(--color-accent)',
                background: 'transparent',
                border: 'none',
                cursor: activeCount === 0 ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-body)',
                fontWeight: 500,
                padding: '4px 4px',
              }}
            >
              Clear filters
            </button>
            <button
              onClick={() => setOpen(false)}
              style={{
                fontSize: 12.5,
                fontWeight: 500,
                background: 'var(--color-accent)',
                color: '#fff',
                border: 'none',
                padding: '6px 14px',
                borderRadius: 6,
                cursor: 'pointer',
                fontFamily: 'var(--font-body)',
              }}
            >
              Apply
            </button>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

function PopoverField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span
        style={{
          fontSize: 11.5,
          color: 'var(--color-text-mid)',
          fontFamily: 'var(--font-body)',
          fontWeight: 500,
        }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}

function FilterSelect({
  value, onChange, options, placeholder, disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder: string;
  disabled?: boolean;
}) {
  const hasValue = !!value;
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      style={{
        width: '100%',
        height: 34,
        padding: '0 30px 0 11px',
        fontSize: 13,
        fontFamily: 'var(--font-body)',
        color: hasValue ? 'var(--color-text)' : 'var(--color-text-mid)',
        background:
          `var(--color-panel) url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6' fill='none'><path d='M1 1l4 4 4-4' stroke='%238894a5' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/></svg>") no-repeat right 11px center`,
        border: `1px solid ${hasValue ? 'var(--color-accent)' : 'var(--color-border)'}`,
        borderRadius: 7,
        appearance: 'none',
        WebkitAppearance: 'none',
        MozAppearance: 'none',
        outline: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        fontWeight: hasValue ? 500 : 400,
        transition: 'border-color 120ms',
      }}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function FilterText({
  value, onChange, placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  const hasValue = !!value;
  return (
    <input
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: '100%',
        height: 34,
        padding: '0 11px',
        fontSize: 13,
        fontFamily: 'var(--font-body)',
        color: 'var(--color-text)',
        background: 'var(--color-panel)',
        border: `1px solid ${hasValue ? 'var(--color-accent)' : 'var(--color-border)'}`,
        borderRadius: 7,
        outline: 'none',
        transition: 'border-color 120ms',
      }}
    />
  );
}
